import { useI18n } from "@core/i18n/i18n";
import { analyticsService, notificationManager } from "@core/service-container";
import { AnalyticsCategory } from "@domain/analytics/analytics-types";
import { ToastContext } from "@ui/components/common/toast-provider";
import { RoutesPath } from "@ui/navigation/routes";
import { useNotification } from "@ui/pages/notification-center/hooks/use-notification";
import { isNil, isNotNil } from "@utils/assert";
import { useObservable } from "micro-observables";
import { useContext, useEffect } from "react";
import { useNavigate } from "react-router";

interface UseNotificationToastHook {
	showNotification: (message: string, onClick: () => void) => void;
}

export const useNotificationToast = (): UseNotificationToastHook => {
	const navigate = useNavigate();
	const { format } = useI18n();
	const isNewUnreadNotificationAvailable = useObservable(notificationManager.isNewUnsentNotificationsAvailable);

	const { formatNotification, onNotificationClicked } = useNotification();

	const { showNotification, clearToasts } = useContext(ToastContext);

	useEffect(() => {
		if (!isNewUnreadNotificationAvailable) return;

		const newUnreadNotificationData = notificationManager.newUnsentNotificationData;
		if (isNil(newUnreadNotificationData)) return;

		const { notification, count } = newUnreadNotificationData;

		if (isNotNil(notification)) {
			showNotification(formatNotification(notification), async () => {
				clearToasts();
				await onNotificationClicked(notification, "toast");
			});
		} else {
			showNotification(format("global.notifications.toast.newNotificationsReceived", { count }), () => {
				clearToasts();
				analyticsService.trackEvent({
					category: AnalyticsCategory.PERMISSIONS,
					action: "clickNotificationToast",
					params: ["notificationCenter"],
				});
				navigate(RoutesPath.NotificationCenter);
			});
		}
	}, [
		format,
		formatNotification,
		navigate,
		showNotification,
		onNotificationClicked,
		isNewUnreadNotificationAvailable,
		clearToasts,
	]);

	return { showNotification };
};
